export const MAX_BULK_ROWS = 500;

export function parseJsonRows(text: string): Record<string, unknown>[] {
  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) {
    return parsed as Record<string, unknown>[];
  }
  if (parsed && typeof parsed === "object" && Array.isArray((parsed as { items?: unknown }).items)) {
    return (parsed as { items: Record<string, unknown>[] }).items;
  }
  throw new Error("JSON must be an array of objects or { items: [...] }");
}

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

export function parseCsvRows(text: string): Record<string, unknown>[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) {
    throw new Error("CSV must include a header row and at least one data row");
  }
  const headers = parseCsvLine(lines[0]).map((h) => h.replace(/^"|"$/g, ""));
  return lines.slice(1).map((line) => {
    const values = parseCsvLine(line).map((v) => v.replace(/^"|"$/g, ""));
    const row: Record<string, unknown> = {};
    headers.forEach((h, i) => {
      row[h] = values[i] ?? "";
    });
    return row;
  });
}

export async function parseBulkFile(file: File): Promise<Record<string, unknown>[]> {
  const text = await file.text();
  const lower = file.name.toLowerCase();
  let rows: Record<string, unknown>[];
  if (lower.endsWith(".json")) {
    rows = parseJsonRows(text);
  } else if (lower.endsWith(".csv")) {
    rows = parseCsvRows(text);
  } else {
    throw new Error("Supported formats: .json, .csv");
  }
  if (rows.length > MAX_BULK_ROWS) {
    throw new Error(`Maximum ${MAX_BULK_ROWS} rows per file`);
  }
  return rows;
}

export function parseUuidArray(value: unknown): string[] | undefined {
  if (Array.isArray(value)) {
    return value.map(String);
  }
  if (typeof value === "string" && value.trim()) {
    return value.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return undefined;
}
